import { useAssessmentV2 } from "../store";
import { effectiveStudyCapacity, effectiveSalaryFloorLpa } from "../types";
import { ArrowRight } from "lucide-react";

/**
 * Bridge screen shown after Part 1 while Part 2 is still under
 * construction. Renders the derived filters so the learner (and we)
 * can sanity-check the MIN logic and salary flex math.
 */
export function EndOfPart1Screen() {
  const { state, reset, goto } = useAssessmentV2();
  const cap = effectiveStudyCapacity(state.part1);
  const floor = effectiveSalaryFloorLpa(state.part1);

  const capLabel: Record<string, string> = {
    none: "No further study",
    short_6mo: "Up to a 6-month course",
    part_time_1_2y: "1–2 years part-time",
    full_degree: "A full degree if it fits",
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="text-xs uppercase tracking-[0.2em] text-primary mb-3">
        Chapter one complete
      </div>
      <h1 className="font-display text-3xl md:text-4xl font-medium leading-tight">
        Here's what that tells us so far.
      </h1>
      <p className="mt-3 text-muted-foreground">
        The honest read from what you just said. Next, we'll get into how you actually work.
      </p>

      <dl className="mt-10 space-y-6">
        <div className="rounded-3xl border border-border bg-card p-5">
          <dt className="text-sm text-muted-foreground">What study feels right</dt>
          <dd className="mt-1 text-xl text-foreground">
            {cap ? capLabel[cap] : "—"}
          </dd>
          <p className="mt-2 text-sm text-muted-foreground">
            The lower of what you'd take on and what you'd actually do when a real start date is attached.
          </p>
        </div>

        <div className="rounded-3xl border border-border bg-card p-5">
          <dt className="text-sm text-muted-foreground">Where salary settles</dt>
          <dd className="mt-1 text-xl text-foreground">
            {floor === null ? "—" : floor === 0 ? "No hard floor" : `≈ ₹${floor.toFixed(1)} LPA`}
          </dd>
          <p className="mt-2 text-sm text-muted-foreground">
            Your stated floor, softened by the flex you'd accept for work you'd really want.
          </p>
        </div>

        <div className="rounded-3xl border border-border bg-card p-5">
          <dt className="text-sm text-muted-foreground">Where you'd work</dt>
          <dd className="mt-1 text-xl text-foreground">
            {state.part1.work_location === "anywhere" && "You'll go where the work is"}
            {state.part1.work_location === "my_city" && "You want it close to home"}
            {state.part1.work_location === "remote_only" && "Remote suits you"}
            {!state.part1.work_location && "—"}
          </dd>
          <p className="mt-2 text-sm text-muted-foreground">
            {state.part1.years_experience ?? 0} year{(state.part1.years_experience ?? 0) === 1 ? "" : "s"} of paid experience so far.
          </p>
        </div>
      </dl>

      <div className="mt-12 flex gap-3">
        <button
          onClick={reset}
          className="min-h-[48px] px-6 rounded-2xl border border-border text-foreground hover:bg-secondary
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Start over
        </button>
        <button
          onClick={() => goto("p2_workday_sort")}
          className="min-h-[48px] px-6 rounded-2xl bg-primary text-primary-foreground font-medium
            inline-flex items-center gap-2
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Keep going <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
